<template>
  <a-drawer
    :visible="isVisible"
    :title="$tm('statistic.filter.selectDepartment')"
    placement="right"
    :closable="false"
    :width="400"
    :body-style="{
      padding: 0
    }"
    :mask-style="{
      backgroundColor: 'transparent'
    }"
    @update:visible="res => $emit('update:is-visible', res)"
  >
    <div class="tree-container">
      <a-tree
        v-model:expandedKeys="expandedKeys"
        v-model:selectedKeys="selectedKeys"
        v-model:checkedKeys="checkedKeys"
        :tree-data="treeData"
        checkable
        check-strictly
        show-icon
        :style="{ 'marginLeft': '16px' }"
        @check="onCheck"
      />
      <div class="drawer-form-action">
        <a-button
          style="margin-right: 8px"
          @click="cancel"
        >
          {{ $t('shared.cancel') }}
        </a-button>
        <a-button
          type="primary"
          :disabled="checkedKeys.checked.length === 0"
          @click="filter"
        >
          {{ $t('shared.ok') }}
        </a-button>
      </div>
    </div>
  </a-drawer>
</template>

<script>
import { computed, defineComponent, ref, toRaw } from 'vue'
import { useStore } from 'vuex'

import {
  getDepartment as getDepartmentApi,
  getHrbpDepartment as getHrbpDepartmentApi,
  getHrbpDepartments as getHrbpDepartmentsApi,
  getMyDepartments as getMyDepartmentsApi
} from '@/graphql/department'

export default defineComponent({
  name: 'StatisticsFilterDrawer',
  components: {},
  props: {
    isLoading: {
      type: Boolean,
      required: true
    },
    isVisible: {
      type: Boolean,
      required: true
    },
    departmentMap: {
      type: Object,
      required: true
    }
  },
  emits: [
    'update:is-visible',
    'filter',
    'update:department-map',
    'update:is-loading'
  ],
  setup (props, { emit }) {
    const store = useStore()
    const expandedKeys = ref([])
    const selectedKeys = ref([])
    const treeData = ref([])
    const checkedKeys = ref({
      checked: []
    })
    const user = computed(() => store.state.user)

    const getMyDepartments = (user.value.isHrbp || user.value.isHrd)
      ? getHrbpDepartmentsApi
      : getMyDepartmentsApi
    const getMyDepartmentApi = (user.value.isHrbp || user.value.isHrd)
      ? getHrbpDepartmentApi
      : getDepartmentApi

    function getChildNodes (departments) {
      const departmentNodes = departments.map(department => ({
        key: department.id,
        title: department.name,
        value: department
      }))

      return [...departmentNodes]
    }

    async function getDepartment (parentDepartment, checkedArr, departmentMap) {
      const res = await getMyDepartmentApi(parentDepartment.value.id, false)
      const subdepartments = getChildNodes(res.childIds, res.userIds)
      subdepartments.forEach(subdepartment => {
        departmentMap[subdepartment.key] = subdepartment.value
        checkedArr.push(subdepartment.value.id)
        departmentMap[subdepartment.key] = subdepartment.value.completeName
      })
      parentDepartment.children = subdepartments
      await Promise.all(subdepartments.map(subdepartment =>
        getDepartment(subdepartment, checkedArr, departmentMap)
      ))
    }

    emit('update:is-loading', true)
    getMyDepartments(false)
      .then(rootDepartments => {
        if (rootDepartments.length === 0) {
          emit('update:is-loading', false)
          return
        }
        const treeDataLocal = [{
          key: rootDepartments[0].id,
          title: rootDepartments[0].name,
          value: rootDepartments[0]
        }]
        const checkedArr = []
        const departmentMap = {}
        return getDepartment(treeDataLocal[0], checkedArr, departmentMap)
          .then(() => {
            treeData.value = [
              ...treeDataLocal[0].children
            ]
            checkedKeys.value.checked = checkedArr
            emit('update:department-map', departmentMap)
            emit('filter', checkedArr)
          })
          .finally(() => {
            emit('update:is-loading', false)
          })
      })

    function filter () {
      emit('filter', [...toRaw(checkedKeys.value.checked)])
      emit('update:is-visible', false)
    }

    function cancel () {
      emit('update:is-visible', false)
    }

    function uncheckKey (key) {
      checkedKeys.value.checked = checkedKeys.value.checked.filter(id => id !== key)
    }

    function onCheck (checkedKeys, e) {
      const checkedKeySet = new Set(checkedKeys.checked)
      checkChildren(e.node.dataRef, checkedKeySet, e.checked)
    }

    function checkChildren (parent, checkedKeySet, isChecked) {
      if (!parent.children) {
        return
      }
      parent.children.forEach(child => {
        const doesExist = checkedKeySet.has(child.value.id)
        if (!doesExist && isChecked) {
          checkedKeys.value.checked.push(child.value.id)
        } else if (doesExist && !isChecked) {
          checkedKeys.value.checked = checkedKeys.value.checked.filter(checkedKey =>
            checkedKey !== child.value.id
          )
        }
        checkChildren(child, checkedKeySet, isChecked)
      })
    }

    return {
      expandedKeys,
      selectedKeys,
      checkedKeys,
      treeData,
      filter,
      cancel,
      uncheckKey,
      onCheck
    }
  }
})
</script>

<style lang="less" scoped>
.tree-container {
  height: calc(100vh - 48px * 2);
  overflow: auto;
}
</style>
